import {useData} from "@hps/hops-react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import RaildaysMap from "Raildays/Components/RaildaysMap.js";
import {
	formatDistanceMilesKm,
	resolveExternalUrl,
	resolveRailwayLocation,
	resolveRailwayMediaUrl,
	resolveRailwayTypeColor,
	slugify
} from "Raildays/RaildaysUtils.js";
import {useCallback, useMemo, useRef} from "react";
import {useOutletContext, useParams} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import CardSection from "Components/Container.js";
import IconButton from "Components/IconButton.js";
import Image from "Components/Image.js";
import Link from "Components/Link.js";
import Loader from "Components/Loader.js";
import RaildaysService from "Services/RaildaysService.js";

import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import XIcon from "@mui/icons-material/X";

const MAP_HEIGHT = "calc(100vh - var(--raildays-content-top))";
const CARD_BODY_PADDING = "clamp(16px, 2.2vw, 32px)";

const haversineMiles = (pointA, pointB) => {
	if (!pointA || !pointB) return null;
	const toRad = deg => (deg * Math.PI) / 180;
	const lat1 = pointA.lat;
	const lon1 = pointA.lng;
	const lat2 = pointB.lat;
	const lon2 = pointB.lng;
	if ([lat1, lon1, lat2, lon2].some(val => Number.isNaN(val))) return null;
	const R = 3958.8;
	const dLat = toRad(lat2 - lat1);
	const dLon = toRad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

const parseISODate = value => {
	if (!value) return null;
	const parts = value.split("-");
	if (parts.length < 3) return null;
	const [year, month, day] = parts.map(Number);
	if (!year || !month || !day) return null;
	return new Date(Date.UTC(year, month - 1, day));
};

const formatOrdinal = value => {
	const mod10 = value % 10;
	const mod100 = value % 100;
	if (mod10 === 1 && mod100 !== 11) return `${value}st`;
	if (mod10 === 2 && mod100 !== 12) return `${value}nd`;
	if (mod10 === 3 && mod100 !== 13) return `${value}rd`;
	return `${value}th`;
};

const formatDateLong = date => {
	if (!date) return "";
	const day = formatOrdinal(date.getUTCDate());
	const month = date.toLocaleString("en-GB", {month: "long", timeZone: "UTC"});
	const year = date.getUTCFullYear();
	return `${day} ${month} ${year}`;
};

const resolveEventTypes = item => {
	if (Array.isArray(item?.EventTypes)) return item.EventTypes;
	return [];
};

const resolveItemName = item => (
	item?.Name || "Untitled"
);

const resolveItemDescription = item => (
	item?.Description || ""
);

const resolveItemText = item => (
	item?.Text || ""
);

const resolveItemImage = item => (
	resolveRailwayMediaUrl(item?.Image || "")
);

const resolveItemId = item => (
	item?.EventId || item?.Id || slugify(resolveItemName(item))
);

const resolveItemLocation = item => {
	const address = item?.Address;
	if (!address) return "";
	const parts = [address.City, address.County, address.Country]
		.filter(Boolean);
	return parts.join(", ");
};

const resolveItemAddress = item => {
	const address = item?.Address;
	if (!address) return "";
	return [
		address.Address,
		address.City,
		address.County,
		address.Postcode,
		address.Country
	].filter(Boolean).join(", ");
};

const resolveItemOrgId = item => (
	item?.OrgId || ""
);

const resolveItemLatLng = item => {
	const location = item?.Location;
	const latitude = Number(location?.Latitude);
	const longitude = Number(location?.Longitude);
	if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
	return {lat: latitude, lng: longitude};
};

const resolveTypeName = type => (
	type?.Name || ""
);

const resolveTypeColor = type => (
	type?.Colour || ""
);

const resolveMarkerColor = (item, railwayTypeColor = "") => {
	if (railwayTypeColor) return railwayTypeColor;
	const railwayMap = item?.Railway?.Map;
	const railwayColor = railwayMap?.Colour;
	if (railwayColor) return railwayColor;
	const types = resolveEventTypes(item);
	if (types.length) {
		const typeColor = resolveTypeColor(types[0]);
		if (typeColor) return typeColor;
	}
	return "#444";
};

const resolveMarkerImage = item => (
	resolveRailwayMediaUrl(
		item?.Railway?.Logo ||
		item?.Logo ||
		item?.Image
	)
);

const resolveSocialUrl = (base, handle) => {
	if (!handle) return "";
	if (handle.startsWith("http")) return handle;
	return `${base}${handle.replace(/^@/, "")}`;
};

const resolveSocialLinks = item => {
	const social = item?.Railway?.SocialMedia || item?.SocialMedia;
	if (!social) return {};
	return {
		twitter: resolveSocialUrl("https://twitter.com/", social?.Twitter),
		facebook: resolveSocialUrl("https://facebook.com/", social?.Facebook),
		instagram: resolveSocialUrl("https://instagram.com/", social?.Instagram)
	};
};

const resolveExternalLink = item => resolveExternalUrl(
	item?.Website ||
	item?.Url ||
	item?.Contact?.Website ||
	item?.Contact?.Url ||
	""
);

const resolveItemRailwayName = item => (
	item?.Railway?.Name ||
	item?.RailwayName ||
	""
);

const resolveRailwayLatLng = railway => {
	const location = railway?.Location;
	const latitude = Number(location?.Latitude);
	const longitude = Number(location?.Longitude);
	if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
	return {lat: latitude, lng: longitude};
};

const takeClosestUniqueByName = (items, limit = 2) => {
	const seen = new Set();
	const unique = [];

	for (const item of items) {
		const key = slugify(item?.name || "");
		if (!key || seen.has(key)) continue;
		seen.add(key);
		unique.push(item);
		if (unique.length >= limit) break;
	}

	return unique;
};

const RaildaysExperienceDetailView = () => {
	const {id} = useParams();
	const mapRef = useRef(null);
	const markerRefs = useRef({});
	const locationContext = useOutletContext() || {};
	const targetLocation = locationContext.targetLocation || null;
	const requestCurrentLocation = locationContext.requestCurrentLocation || (() => {});
	const locationLoading = Boolean(locationContext.locationLoading);

	const {data: experiencesData, error: experiencesError, loading: experiencesLoading} = useData(
		useCallback(() => RaildaysService.getEvents({Category: 3}), [])
	);
	const {data: railwaysData} = useData(
		useCallback(() => RaildaysService.getRailways(), [])
	);

	const experiences = useMemo(() => {
		const resolved = experiencesData;
		if (!resolved) return [];
		if (Array.isArray(resolved)) return resolved;
		return [];
	}, [experiencesData]);

	const railways = useMemo(() => {
		const resolved = railwaysData;
		if (!resolved) return [];
		if (Array.isArray(resolved)) return resolved;
		if (Array.isArray(resolved.objects)) return resolved.objects;
		return [];
	}, [railwaysData]);

	const railwaysByOrgId = useMemo(() => {
		const index = new Map();
		railways.forEach(item => {
			const orgId = item?.Id;
			if ((orgId === undefined) || (orgId === null)) return;
			index.set(String(orgId), {
				name: item?.Name || "",
				typeColor: resolveRailwayTypeColor(item)
			});
		});
		return index;
	}, [railways]);

	const experience = useMemo(() => (
		experiences.find(item => String(item?.EventId || item?.Id) === String(id))
	), [experiences, id]);

	const railway = useMemo(() => {
		if (!experience) return null;
		const orgId = experience?.OrgId;
		if (!orgId) return null;
		return railways.find(item => String(item?.Id) === String(orgId));
	}, [experience, railways]);

	const name = resolveItemName(experience);
	const image = resolveItemImage(experience);
	const descriptionText = resolveItemDescription(experience);
	const longText = resolveItemText(experience);
	const location = resolveItemLocation(experience);
	const address = resolveItemAddress(experience);
	const types = resolveEventTypes(experience);
	const externalLink = resolveExternalLink(experience);
	const socialLinks = resolveSocialLinks(experience);
	const coords = resolveItemLatLng(experience);
	const dateStart = parseISODate(experience?.StartDate);
	const dateEnd = parseISODate(experience?.EndDate) || dateStart;
	const heading = name || "Experience details";
	const experienceOrgId = resolveItemOrgId(experience);
	const railwayMeta = railwaysByOrgId.get(String(experienceOrgId || ""));
	const railwayName =
		resolveItemRailwayName(experience) ||
		railway?.Name ||
		railwayMeta?.name ||
		"";

	const dateLabel = dateStart && dateEnd ?
		(dateStart.getTime() === dateEnd.getTime() ?
			formatDateLong(dateStart) :
			`${formatDateLong(dateStart)} - ${formatDateLong(dateEnd)}`) :
		"";

	const mapMarkers = useMemo(() => (
		experience && coords ?
			[{
				id: experience?.EventId || experience?.Id || slugify(name),
				name,
				railwayName,
				location,
				subname: [railwayName, location].filter(Boolean).join(", "),
				dateLabel,
				typeLabel: types.map(resolveTypeName).filter(Boolean).join(", "),
				position: [coords.lat, coords.lng],
				color: resolveMarkerColor(experience, railwayMeta?.typeColor),
				image: resolveMarkerImage(experience),
				actionLabel: "View experience",
				link: `/experiences/${experience?.EventId || experience?.Id || slugify(name)}`
			}] :
			[]
	), [experience, coords, name, railwayName, location, dateLabel, railwayMeta, types]);

	const mapBounds = useMemo(() => mapMarkers.map(marker => marker.position), [mapMarkers]);

	const handleViewOnMap = useCallback(() => {
		if (!experience) return;
		const marker = markerRefs.current[experience?.EventId || experience?.Id || slugify(name)];
		if (!marker) return;
		marker.openPopup();
		const map = mapRef.current;
		if (map) {
			map.setView(marker.getLatLng(), Math.max(map.getZoom(), 10), {animate: true});
		}
	}, [experience, name]);

	const handleUseLocation = useCallback(() => {
		requestCurrentLocation();
	}, [requestCurrentLocation]);

	const distance = useMemo(() => {
		if (!coords || !targetLocation) return null;
		const toRad = deg => (deg * Math.PI) / 180;
		const lat1 = targetLocation.lat;
		const lon1 = targetLocation.lng;
		const lat2 = coords.lat;
		const lon2 = coords.lng;
		const R = 3958.8;
		const dLat = toRad(lat2 - lat1);
		const dLon = toRad(lon2 - lon1);
		const a =
			Math.sin(dLat / 2) ** 2 +
			Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c;
	}, [coords, targetLocation]);

	const currentExperienceId = String(experience?.EventId || experience?.Id || "");
	const currentRailwayId = String(
		experience?.OrgId || ""
	);

	const nearbyRailways = useMemo(() => {
		if (!coords) return [];
		return railways
			.filter(item => String(item?.Id) !== currentRailwayId)
			.map(item => {
				const latLng = resolveRailwayLatLng(item);
				const nearDistance = latLng ? haversineMiles(coords, latLng) : null;
				return {
					id: String(item?.Id || slugify(item?.Name || "railway")),
					name: item?.Name || "Railway",
					slug: slugify(item?.Name || "railway"),
					location: resolveRailwayLocation(item),
					image: resolveRailwayMediaUrl(
						item?.Hero || item?.Image || item?.Logo
					),
					distance: nearDistance
				};
			})
			.filter(item => typeof item.distance === "number" && !Number.isNaN(item.distance))
			.sort((a, b) => a.distance - b.distance)
			.slice(0, 2);
	}, [coords, currentRailwayId, railways]);

	const nearbyExperiences = useMemo(() => {
		if (!coords) return [];
		const ordered = experiences
			.filter(item => String(item?.EventId || item?.Id || "") !== currentExperienceId)
			.map(item => {
				const latLng = resolveItemLatLng(item);
				const nearDistance = latLng ? haversineMiles(coords, latLng) : null;
				return {
					id: resolveItemId(item),
					name: resolveItemName(item),
					railwayName:
							resolveItemRailwayName(item) ||
							railwaysByOrgId.get(String(resolveItemOrgId(item) || ""))?.name ||
							"",
					location: resolveItemLocation(item),
					image: resolveItemImage(item),
					distance: nearDistance
				};
			})
			.filter(item => typeof item.distance === "number" && !Number.isNaN(item.distance))
			.sort((a, b) => a.distance - b.distance);

		return takeClosestUniqueByName(ordered, 2);
	}, [coords, currentExperienceId, experiences, railwaysByOrgId]);

	if (experiencesLoading) {
		return (
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					mx: {xs: 2, md: 3, lg: 5},
					mb: {xs: 2, md: 3, lg: 5},
					backgroundColor: "#fff",
					borderRadius: 2
				}}
			>
				<Container maxWidth={false} sx={{py: {xs: 4, md: 6}}}>
					<Stack alignItems="center" sx={{py: 8}}>
						<Loader />
					</Stack>
				</Container>
			</Box>
		);
	}

	if (experiencesError && !experience) {
		return (
			<Container maxWidth="lg" sx={{py: {xs: 6, md: 10}}}>
				<Alert severity="error">We could not load this experience right now.</Alert>
			</Container>
		);
	}

	if (!experience && !experiencesLoading) {
		return (
			<Container maxWidth="lg" sx={{py: {xs: 6, md: 10}}}>
				<Alert severity="warning">We could not find that experience.</Alert>
			</Container>
		);
	}

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				mx: {xs: 2, md: 3, lg: 5},
				mb: {xs: 2, md: 3, lg: 5},
				backgroundColor: "#fff",
				borderRadius: 2
			}}
		>
			<Container maxWidth={false} sx={{py: {xs: 4, md: 6}}}>
				<Box sx={{px: {xs: 2, md: 4, lg: 6}}}>
					<Breadcrumbs sx={{textTransform: "uppercase", color: "#e2b534"}}>
						<Button label="Home" uri="/" style={{color: "#e2b534"}} variant="text" />
						<Button label="Experiences" uri="/experiences" style={{color: "#e2b534"}} variant="text" />
						<Typography color="#e2b534">{heading}</Typography>
					</Breadcrumbs>
					<Typography variant="h4" sx={{mt: 2, fontWeight: 800, textTransform: "uppercase"}}>
						{heading}
					</Typography>
				</Box>

				<Box sx={{px: {xs: 2, md: 4, lg: 6}, mt: 4, display: {xs: "block", md: "flex"}, gap: 3}}>
					<Box sx={{width: {md: "60%"}}}>
						<Card
							px={0}
							py={0}
							style={{
								backgroundColor: "#fff",
								borderRadius: 12,
								boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
								overflow: "hidden"
							}}>
							{image && (
								<Image
									alt={name}
									noError={true}
									noLoader={true}
									src={image}
									style={{width: "100%", aspectRatio: "16 / 9"}}
									imgStyles={{width: "100%", height: "100%", display: "block"}} />
							)}
							<CardSection
								display="block"
								px={0}
								py={0}
								style={{padding: CARD_BODY_PADDING}}>
								<Stack
									direction={{xs: "column", md: "row"}}
									spacing={2}
									alignItems={{xs: "flex-start", md: "center"}}
									justifyContent="space-between"
								>
									<Box>
										<Typography variant="h5" sx={{fontWeight: 900, color: "#e2b534"}}>
											{name}
										</Typography>
										{railwayName && (
											<Typography variant="h6" sx={{fontWeight: 800, color: "#334155", lineHeight: 1.2, mt: 0.25}}>
												{railwayName}
											</Typography>
										)}
										{location && (
											<Typography color="text.secondary" sx={{fontWeight: 700}}>
												{location}
											</Typography>
										)}
									</Box>
									<Button label="View on Map" variant="outlined" onClick={handleViewOnMap} />
								</Stack>

								{descriptionText && (
									<Typography sx={{mt: 3, fontWeight: 700}}>
										{descriptionText}
									</Typography>
								)}
								{longText && (
									<Typography sx={{mt: 2}}>
										{longText}
									</Typography>
								)}

								<Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 3}} alignItems="center" justifyContent="space-between">
									{externalLink ?
										(
											<Button
												label="View More Information"
												variant="contained"
												onClick={() => window.open(externalLink, "_blank")} />
										) :
										(
											<Box />
										)}

									<Stack direction="row" spacing={1} alignItems="center">
										{socialLinks.twitter && (
											<Link uri={socialLinks.twitter}>
												<IconButton
													icon={XIcon}
													label="X"
													style={{border: "1px solid #e2b534", color: "#e2b534"}} />
											</Link>
										)}
										{socialLinks.facebook && (
											<Link uri={socialLinks.facebook}>
												<IconButton
													icon={FacebookIcon}
													label="Facebook"
													style={{border: "1px solid #e2b534", color: "#e2b534"}} />
											</Link>
										)}
										{socialLinks.instagram && (
											<Link uri={socialLinks.instagram}>
												<IconButton
													icon={InstagramIcon}
													label="Instagram"
													style={{border: "1px solid #e2b534", color: "#e2b534"}} />
											</Link>
										)}
									</Stack>
								</Stack>
							</CardSection>

							<Divider />

							<CardSection
								display="block"
								px={0}
								py={0}
								style={{padding: `16px ${CARD_BODY_PADDING} ${CARD_BODY_PADDING}`}}>
								<Stack spacing={2}>
									{dateLabel && (
										<Stack direction={{xs: "column", md: "row"}} spacing={2}>
											<Box sx={{minWidth: 140}}>
												<Typography variant="subtitle2" color="text.secondary">
													Date{dateStart && dateEnd && dateStart.getTime() !== dateEnd.getTime() ? "s" : ""}
												</Typography>
											</Box>
											<Typography>{dateLabel}</Typography>
										</Stack>
									)}

									{!!types.length && (
										<Stack direction={{xs: "column", md: "row"}} spacing={2}>
											<Box sx={{minWidth: 140}}>
												<Typography variant="subtitle2" color="text.secondary">
													Experience Type(s)
												</Typography>
											</Box>
											<Stack spacing={0.5}>
												{types.map(type => (
													<Typography key={type?.Id || resolveTypeName(type)}>
														{resolveTypeName(type)}
													</Typography>
												))}
											</Stack>
										</Stack>
									)}

									{(address || location) && (
										<Stack direction={{xs: "column", md: "row"}} spacing={2}>
											<Box sx={{minWidth: 140}}>
												<Typography variant="subtitle2" color="text.secondary">
													Location
												</Typography>
											</Box>
											<Typography>{address || location}</Typography>
										</Stack>
									)}

									<Stack direction={{xs: "column", md: "row"}} spacing={2} alignItems={{xs: "flex-start", md: "center"}}>
										<Box sx={{minWidth: 140}}>
											<Typography variant="subtitle2" color="text.secondary">
												Distance
											</Typography>
										</Box>
										{typeof distance === "number" && !Number.isNaN(distance) ?
											(
												<Typography>{formatDistanceMilesKm(distance)}</Typography>
											) :
											targetLocation ?
												(
													<Typography color="text.secondary">Distance unavailable</Typography>
												) :
												(
													<Button
														label="Use My Location"
														variant="outlined"
														size="small"
														onClick={handleUseLocation}
														disabled={locationLoading} />
												)}
									</Stack>

									{externalLink && (
										<Stack direction={{xs: "column", md: "row"}} spacing={2}>
											<Box sx={{minWidth: 140}}>
												<Typography variant="subtitle2" color="text.secondary">
													Website
												</Typography>
											</Box>
											<Button
												label={externalLink}
												variant="text"
												onClick={() => window.open(externalLink, "_blank")} />
										</Stack>
									)}

									{experience?.Contact?.Phone ?
										(
											<Stack direction={{xs: "column", md: "row"}} spacing={2}>
												<Box sx={{minWidth: 140}}>
													<Typography variant="subtitle2" color="text.secondary">
													Phone
													</Typography>
												</Box>
												<Typography>{experience?.Contact?.Phone}</Typography>
											</Stack>
										) :
										null}

									{railway && (
										<Stack direction={{xs: "column", md: "row"}} spacing={2}>
											<Box sx={{minWidth: 140}}>
												<Typography variant="subtitle2" color="text.secondary">
													Railway
												</Typography>
											</Box>
											<Button
												label="Visit Railway"
												uri={`/railways/${slugify(railway?.Name || "")}`}
												variant="contained" />
										</Stack>
									)}

									{nearbyRailways.length > 0 && (
										<Stack direction={{xs: "column", md: "row"}} spacing={2}>
											<Box sx={{minWidth: 140}}>
												<Typography variant="subtitle2" color="text.secondary">
													Nearby Railways
												</Typography>
											</Box>
											<Box
												sx={{
													flexGrow: 1,
													display: "grid",
													gridTemplateColumns: {xs: "1fr", md: "repeat(2, minmax(0, 1fr))"},
													gap: 1.5
												}}>
												{nearbyRailways.map(item => (
													<Card
														key={item.id}
														outlined={true}
														px={0}
														py={0}
														style={{
															height: "100%",
															borderRadius: 10,
															overflow: "hidden",
															boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)"
														}}>
														{item.image && (
															<Image
																alt={item.name}
																noError={true}
																noLoader={true}
																src={item.image}
																style={{width: "100%", aspectRatio: "16 / 9"}}
																imgStyles={{width: "100%", height: "100%", display: "block"}} />
														)}
														<CardSection display="block" px={0} py={0} style={{padding: 12}}>
															<Typography sx={{fontWeight: 800}}>{item.name}</Typography>
															{item.location && (
																<Typography color="text.secondary" sx={{fontSize: 13}}>
																	{item.location}
																</Typography>
															)}
															<Typography color="text.secondary" sx={{fontSize: 13, mt: 0.5}}>
																{formatDistanceMilesKm(item.distance)}
															</Typography>
															<Box sx={{mt: 1.25}}>
																<Button
																	label="Visit Railway"
																	uri={`/railways/${item.slug}`}
																	variant="contained"
																	size="small" />
															</Box>
														</CardSection>
													</Card>
												))}
											</Box>
										</Stack>
									)}

									{nearbyExperiences.length > 0 && (
										<Stack direction={{xs: "column", md: "row"}} spacing={2}>
											<Box sx={{minWidth: 140}}>
												<Typography variant="subtitle2" color="text.secondary">
													Other Experiences Nearby
												</Typography>
											</Box>
											<Box
												sx={{
													flexGrow: 1,
													display: "grid",
													gridTemplateColumns: {xs: "1fr", md: "repeat(2, minmax(0, 1fr))"},
													gap: 1.5
												}}>
												{nearbyExperiences.map(item => (
													<Card
														key={item.id}
														outlined={true}
														px={0}
														py={0}
														style={{
															height: "100%",
															borderRadius: 10,
															overflow: "hidden",
															boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)"
														}}>
														{item.image && (
															<Image
																alt={item.name}
																noError={true}
																noLoader={true}
																src={item.image}
																style={{width: "100%", aspectRatio: "16 / 9"}}
																imgStyles={{width: "100%", height: "100%", display: "block"}} />
														)}
														<CardSection display="block" px={0} py={0} style={{padding: 12}}>
															<Typography sx={{fontWeight: 800}}>{item.name}</Typography>
															{item.railwayName && (
																<Typography variant="subtitle2" sx={{fontWeight: 700, color: "#334155", lineHeight: 1.2}}>
																	{item.railwayName}
																</Typography>
															)}
															{item.location && (
																<Typography color="text.secondary" sx={{fontSize: 13}}>
																	{item.location}
																</Typography>
															)}
															<Typography color="text.secondary" sx={{fontSize: 13, mt: 0.5}}>
																{formatDistanceMilesKm(item.distance)}
															</Typography>
															<Box sx={{mt: 1.25}}>
																<Button
																	label="View More Information"
																	uri={`/experiences/${item.id}`}
																	variant="contained"
																	size="small" />
															</Box>
														</CardSection>
													</Card>
												))}
											</Box>
										</Stack>
									)}
								</Stack>
							</CardSection>
						</Card>
					</Box>

					<Box sx={{width: {md: "40%"}, alignSelf: "stretch"}}>
						<Box
							sx={{
								position: {md: "sticky"},
								top: {md: "var(--raildays-content-top)"},
								height: {md: MAP_HEIGHT},
								minHeight: {md: MAP_HEIGHT}
							}}
						>
							<RaildaysMap
								markers={mapMarkers}
								bounds={mapBounds}
								markerShape="circle"
								height="100%"
								mapRef={mapRef}
								markerRefs={markerRefs}
							/>
						</Box>
					</Box>
				</Box>
			</Container>
		</Box>
	);
};

export default RaildaysExperienceDetailView;
